'use client'

import * as React from 'react'
import { cn } from '../../utils/cn'
import { ArrowRight, Play } from 'lucide-react'

interface HeroBadge {
  text: string
  href?: string
}

interface HeroCTA {
  text: string
  href: string
}

interface HeroScreenshot {
  src: string
  alt: string
}

interface HeroWithProductProps {
  badge?: HeroBadge
  headline: React.ReactNode
  subheadline: React.ReactNode
  primaryCTA: HeroCTA
  secondaryCTA?: HeroCTA
  screenshot?: HeroScreenshot
  avatars?: string[]
  productShowcase?: React.ReactNode
}

export function HeroWithProduct({
  badge,
  headline,
  subheadline,
  primaryCTA,
  secondaryCTA,
  screenshot,
  productShowcase,
  avatars
}: HeroWithProductProps) {
  return (
    <section className="relative min-h-screen pt-24 pb-24 overflow-hidden">
      {/* Grid background pattern */}
      <div className="absolute inset-0 dot-pattern opacity-20" />

      {/* Accent glow at top */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] opacity-30"
        style={{
          background: `radial-gradient(ellipse at center, var(--accent-glow), transparent 70%)`
        }}
      />

      <div className="landing-container relative z-10">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto mb-16">
          {/* Badge */}
          {badge && (
            <a
              href={badge.href}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-1.5 mb-6',
                'text-sm font-medium',
                'bg-[var(--landing-bg-card)] border border-[var(--landing-border)]',
                'rounded-full',
                'text-[var(--landing-text-secondary)]',
                'hover:border-[var(--landing-border-strong)]',
                'transition-all duration-200'
              )}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent)] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--accent)]" />
              </span>
              {badge.text}
              <ArrowRight className="h-3 w-3" />
            </a>
          )}

          {/* Headline */}
          <h1 className="headline-xl mb-6">
            {headline}
          </h1>

          {/* Subheadline */}
          <p className="body-lg max-w-readable mb-10">
            {subheadline}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <a
              href={primaryCTA.href}
              className={cn(
                'inline-flex items-center justify-center gap-2',
                'px-8 py-4 text-base font-semibold rounded-xl',
                'bg-[var(--accent)] text-white',
                'hover:bg-[var(--accent-hover)]',
                'transition-all duration-200',
                'shadow-lg shadow-[var(--accent-glow)]'
              )}
            >
              {primaryCTA.text}
              <ArrowRight className="h-5 w-5" />
            </a>
            {secondaryCTA && (
              <a
                href={secondaryCTA.href}
                className={cn(
                  'inline-flex items-center justify-center gap-2',
                  'px-8 py-4 text-base font-medium rounded-xl',
                  'bg-transparent border border-[var(--landing-border)]',
                  'text-[var(--landing-text-primary)]',
                  'hover:border-[var(--landing-border-strong)] hover:bg-[var(--landing-bg-hover)]',
                  'transition-all duration-200'
                )}
              >
                <Play className="h-4 w-4" />
                {secondaryCTA.text}
              </a>
            )}
          </div>

          {/* Avatar Stack (Social Proof) */}
          {avatars && avatars.length > 0 && (
            <div className="flex items-center gap-3 mt-8">
              <div className="flex -space-x-3">
                {avatars.slice(0, 5).map((avatar, index) => (
                  <img
                    key={index}
                    src={avatar}
                    alt=""
                    className={cn(
                      'w-10 h-10 rounded-full',
                      'border-2 border-[var(--landing-bg)]',
                      'ring-2 ring-[var(--landing-border)]'
                    )}
                  />
                ))}
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-[var(--landing-text-primary)]">
                  Trusted by {avatars.length > 1000 ? '1,000+' : avatars.length} teams
                </p>
                <p className="text-xs text-[var(--landing-text-muted)]">
                  From startups to enterprises
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Product Showcase */}
        {productShowcase ? (
          <div className="relative">
            {productShowcase}
          </div>
        ) : screenshot ? (
          <div className="relative max-w-6xl mx-auto">
            {/* Browser Chrome */}
            <div className="browser-chrome">
              <div className="browser-chrome-bar">
                <div className="browser-chrome-dot" />
                <div className="browser-chrome-dot" />
                <div className="browser-chrome-dot" />
                <div className="flex-1 mx-4">
                  <div className="h-6 bg-[var(--landing-bg)] rounded-md" />
                </div>
              </div>
              <img
                src={screenshot.src}
                alt={screenshot.alt}
                className="w-full h-auto"
              />
            </div>

            {/* Subtle shadow beneath */}
            <div
              className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-3/4 h-16 opacity-50 blur-2xl"
              style={{ background: 'var(--accent-glow)' }}
            />
          </div>
        ) : null}
      </div>
    </section>
  )
}
